import React, { useEffect, useState } from 'react';
import { Home, Package, TrendingUp, AlertCircle, Settings, Coffee, CreditCard, X, BookOpen, Globe } from 'lucide-react';
import { useDeviceDetect } from '../../hooks/useDeviceDetect';
import { apiService } from '../../services/api';

interface SidebarProps {
  activeView: string;
  onViewChange: (view: string) => void;
  userRole?: 'admin' | 'user';
  isOpen?: boolean;
  onClose?: () => void;
  productos?: never;
}

const Sidebar: React.FC<SidebarProps> = ({ activeView, onViewChange, userRole = 'user', isOpen = false, onClose }) => {
  const { isTablet, isMobile } = useDeviceDetect();
  const [alertCount, setAlertCount] = useState(0);
  
  useEffect(() => {
    const fetchAlertCount = async () => {
      try {
        const alerts = await apiService.getAllAlerts();
        setAlertCount(alerts.length);
      } catch (error) {
        console.error('Error fetching alert count:', error);
        setAlertCount(0);
      }
    };

    fetchAlertCount();
    
    const interval = setInterval(fetchAlertCount, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Define all menu items with their required roles
  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, allowedRoles: ['admin'] },
    { id: 'inventario', label: 'Inventario', icon: Package, allowedRoles: ['admin', 'user'] },
    { id: 'ventas', label: 'Ventas Clip', icon: CreditCard, allowedRoles: ['admin'] },
    { id: 'recetas', label: 'Recetas', icon: BookOpen, allowedRoles: ['admin', 'user'] },
    { id: 'consumo', label: 'Consumo Personal', icon: Coffee, allowedRoles: ['admin', 'user'] },
    { id: 'movimientos', label: 'Movimientos', icon: TrendingUp, allowedRoles: ['admin'] },
    { id: 'plataformas', label: 'Plataformas', icon: Globe, allowedRoles: ['admin'] },
    { id: 'alertas', label: 'Alertas', icon: AlertCircle, allowedRoles: ['admin', 'user'], badge: alertCount },
    { id: 'configuracion', label: 'Configuración', icon: Settings, allowedRoles: ['admin', 'user'] }
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => item.allowedRoles.includes(userRole));

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (isTablet || isMobile) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        ${(isTablet || isMobile) ? 'fixed' : 'static'} inset-y-0 left-0 z-50
        bg-white shadow-lg ${!(isTablet || isMobile) ? 'shadow-sm' : ''} border-r border-light-gray/20 
        w-64 min-h-screen transform transition-transform duration-300 ease-in-out
        ${(isTablet || isMobile) ? (isOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
      `}>
        {/* Close button for mobile */}
        {onClose && (isTablet || isMobile) && (
          <div className="flex justify-end p-4">
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-warm-brown/10 text-warm-brown hover:bg-warm-brown/20 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        )}
        
        <nav className="mt-8 lg:mt-8">
          <ul className="space-y-1 px-4">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            const hasBadge = item.badge && item.badge > 0;
            
            return (
              <li key={item.id}>
                <button
                  onClick={() => {
                    onViewChange(item.id);
                    if (onClose) onClose();
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-accent text-cream'
                      : 'text-medium-gray hover:bg-cream hover:text-dark-text'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon size={20} className="mr-3" />
                    {item.label}
                  </div>
                  {hasBadge && (
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                      isActive 
                        ? 'bg-cream text-accent' 
                        : 'bg-red-500 text-white'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
    </>
  );
};

export default Sidebar;